import { ButtonMenu } from '../../components/ButtonMenu.component';
import { MenuItem } from '@material-ui/core';
import React from 'react';
import FileSaver from 'file-saver';
import { humanizeKind } from '../../activities/utils/translations';
import { countBookingPeople } from '@crm/utils';
import dayjs from 'dayjs';

/**
 * Interface pour les props du composant ViewActions
 */
interface Props {
  reservations?: CRM.ReservationDetailed[];
}

/**
 * Composant de menu d'actions pour les réservations
 * 
 * Ce composant :
 * - Affiche un menu déroulant avec les actions disponibles
 * - Propose l'export CSV des réservations
 * - Utilise ButtonMenu pour l'interface utilisateur
 * 
 * Logique métier :
 * - Export CSV avec formatage spécifique des données
 * - Gestion des numéros de téléphone avec préfixe +
 * - Formatage des dates et heures
 * - Comptage des participants via countBookingPeople
 * 
 * @param reservations Liste des réservations détaillées (optionnelle)
 * @returns Composant React du menu d'actions
 */
export const ViewActions: React.FC<Props> = ({ reservations = [] }) => {
  return (
    <ButtonMenu>
      <MenuItem onClick={() => exportCSV(reservations)}>Export .csv</MenuItem>
    </ButtonMenu>
  );
};

/**
 * Fonction d'export CSV des réservations
 * 
 * Cette fonction :
 * - Génère un fichier CSV avec les données des réservations
 * - Inclut les en-têtes de colonnes
 * - Formate chaque réservation en ligne CSV
 * - Télécharge automatiquement le fichier
 * 
 * @param reservations Liste des réservations à exporter
 */
const exportCSV = (reservations: CRM.ReservationDetailed[]) => {
  const parts = [
    'type; date; email; nom; prénom; téléphone; activité; nbr participants\n',
    ...reservations.map(asLine),
  ];
  const blob = new Blob(parts, {
    type: 'text/csv;charset=utf-8',
  });
  FileSaver.saveAs(blob, 'reservations.csv');
};

/**
 * Fonction de formatage d'une réservation en ligne CSV
 * 
 * Cette fonction :
 * - Convertit une réservation en ligne CSV formatée
 * - Gère les numéros de téléphone avec échappement du préfixe +
 * - Formate la date d'activité en local
 * - Compte les participants via countBookingPeople
 * 
 * @param r Réservation détaillée à formater
 * @returns Ligne CSV formatée
 */
const asLine = (r: CRM.ReservationDetailed) =>
  [
    humanizeKind(r.activity.kind),
    dayjs(r.activityDate).local().format('YYYY-MM-DD HH:mm'),
    r.customer.email,
    r.customer.name,
    r.customer.firstname,
    (r.customer.phone || '').startsWith('+')
      ? `\\${r.customer.phone}`
      : r.customer.phone,
    r.activity.name,
    countBookingPeople(r.people),
    '\n',
  ].join(';');
